import { Text } from 'src/modules/shared/components/atoms/text';
import React from 'react';
import { type SectionKey } from '../../../../types/funnels/statuses/status-description.types';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';

// TODO: check when implementing i18n
export const SECTION_ORDER: Array<SectionKey> = [
  'entrada',
  'objetivo',
  'pasos',
  'excepciones',
  'seguimiento',
];

// TODO: check when implementing i18n
export const SECTION_LABEL: Record<SectionKey, string> = {
  entrada: 'Condición de Entrada',
  objetivo: 'Objetivo',
  pasos: 'Pasos',
  excepciones: 'Excepciones',
  seguimiento: 'Protocolo de Seguimiento',
};

// TODO: check when implementing i18n
export const SECTION_PLACEHOLDER: Record<SectionKey, string> = {
  entrada:
    'Describe en 1-2 líneas qué tipo de conversaciones entran a este estado y bajo qué condición.',
  objetivo:
    'Describe en 1-2 líneas qué se debe lograr en este estado antes de mover la conversación.',
  pasos:
    'Escribe el flujo principal con pasos numerados (1, 2, 3...), usando sangrías y guiones para ramas condicionales.',
  excepciones:
    'Enumera casos especiales que se escapan del flujo principal y cómo deben manejarse.',
  seguimiento:
    'Define reglas y tiempos de seguimiento (tareas, recordatorios, secuencias de mensajes) para este estado.',
};

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Maps a raw header string to its corresponding SectionKey.
 * Normalizes the input by removing accents and extra whitespace,
 * then matches against known section headers.
 *
 * @param rawHeader - The raw header text to normalize and match
 * @returns The corresponding SectionKey, or undefined if no match
 */
export const headerToKey = (rawHeader: string): SectionKey | undefined => {
  const header = normalize(rawHeader);

  if (header === 'condicion de entrada') return 'entrada';
  if (header === 'objetivo') return 'objetivo';
  if (header === 'pasos') return 'pasos';
  if (header === 'excepciones') return 'excepciones';
  if (header === 'protocolo de seguimiento' || header === 'seguimiento') {
    return 'seguimiento';
  }

  return undefined;
};

/**
 * Returns a tooltip component for a given section, providing guidance on content
 * and format requirements with examples.
 *
 * @param section - The section key to get tooltip content for
 * @returns A React node containing the tooltip content
 */
export const getSectionTooltipContent = (
  section: SectionKey,
  t?: I18nContextValue['t']
): React.ReactNode => {
  const tooltips: Record<SectionKey, React.ReactNode> = {
    entrada: (
      <div className="flex flex-col gap-2 p-2">
        <Text size="tiny" weight="medium">
          Define la condición de entrada
        </Text>
        <Text size="tiny">
          • <span className="font-medium">Qué tipo</span> de conversaciones
          llegan
        </Text>
        <Text size="tiny">
          • <span className="font-medium">Cuándo/bajo qué</span> condición
        </Text>
        <Text size="tiny">
          <span className="font-medium">Máximo 1-2 líneas</span> ❗
        </Text>
        <div className="mt-1 border-t border-gray-200 pt-1">
          <Text size="tiny">
            <span className="font-medium">Ej:</span>{' '}
            <span className="italic">
              Clientes que solicitaron información sobre un producto específico
              en nuestra web
            </span>
          </Text>
        </div>
      </div>
    ),
    objetivo: (
      <div className="flex flex-col gap-2 p-2">
        <Text size="tiny" weight="medium">
          Define la condición de salida
        </Text>
        <Text size="tiny">
          •{' '}
          <span className="font-medium">Qué se debe lograr en este estado</span>
        </Text>
        <Text size="tiny">
          <span className="font-medium">Máximo 1-2 líneas</span> ❗
        </Text>
        <div className="mt-1 border-t border-gray-200 pt-1">
          <Text size="tiny">
            <span className="font-medium">Ej:</span>{' '}
            <span className="italic">
              Recopilar nombre, teléfono y email del cliente antes de derivar al
              asesor experto
            </span>
          </Text>
        </div>
      </div>
    ),
    pasos: (
      <div className="flex flex-col gap-2 p-2">
        <Text size="tiny" weight="medium">
          Flujo principal paso a paso
        </Text>
        <Text size="tiny">
          • <span className="font-medium">1,2,3...</span> para pasos
          secuenciales (verbos de acción)
        </Text>
        <Text size="tiny">
          • <span className="font-medium">Guiones (-)</span> para ramas
          condicionales (Si... entonces...)
        </Text>
        <Text size="tiny">
          • <span className="font-medium">Sangría</span> para mostrar jerarquía
        </Text>
        <Text size="tiny">
          ❗{' '}
          <span className="font-medium">
            SIEMPRE termina moviendo a otro estado
          </span>
        </Text>
        <div className="mt-1 border-t border-gray-200 pt-1">
          <Text size="tiny">
            <span className="font-medium">Ej:</span>{' '}
            <span className="italic whitespace-pre-wrap">
              {t?.('settings.funnels.statusDescription.example.main') ?? ''}
            </span>
          </Text>
        </div>
      </div>
    ),
    excepciones: (
      <div className="flex flex-col gap-2 p-2">
        <Text size="tiny" weight="medium">
          Casos especiales que se escapan del flujo principal
        </Text>
        <div className="mt-1 border-t border-gray-200 pt-1">
          <Text size="tiny">
            <span className="font-medium">Ej:</span>{' '}
            <span className="italic whitespace-pre-wrap">
              {`\n- Si nuestro mensaje inicial falla en el envío, debes mover la conversación al estado "Asistencia humana".`}
            </span>
          </Text>
        </div>
      </div>
    ),
    seguimiento: (
      <div className="flex flex-col gap-2 p-2">
        <Text size="tiny" weight="medium">
          Manejo de tareas para realizar seguimiento
        </Text>
        <div className="mt-1 border-t border-gray-200 pt-1">
          <Text size="tiny">
            <span className="font-medium">Ej:</span>{' '}
            <span className="italic whitespace-pre-wrap">
              {`\n- Si no hay respuesta del contacto a nuestro último mensaje, ejecuta la siguiente secuencia:\n    - 30min ("Pudiste ver mi último mensaje?")\n    - 3h ("Sigo aquí si quieres continuar conversando sobre X")\n    - 24h (mueve a "Perdidos")`}
            </span>
          </Text>
        </div>
      </div>
    ),
  };
  return tooltips[section];
};
